import { create } from 'zustand';
import { persist } from 'zustand/middleware';

const MAX_ENTRIES_PER_KEY = 3;
const MAX_KEYS = 50;
const TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

export interface TestDataEntry {
  payload: string;
  schemaHash: string;
  lastUsed: number;
}

type TestDataState = {
  version: 1;
  entries: Record<string, TestDataEntry[]>;
};

type TestDataActions = {
  getPayload: (workflowName: string, triggerName: string) => TestDataEntry | undefined;
  setPayload: (workflowName: string, triggerName: string, entry: TestDataEntry) => void;
  clearPayload: (workflowName: string, triggerName: string) => void;
  cleanup: () => void;
};

type TestDataStore = TestDataState & TestDataActions;

function makeKey(workflowName: string, triggerName: string): string {
  return `${workflowName}:${triggerName}`;
}

/** Evict keys until at most MAX_KEYS remain, keeping those with the most recent lastUsed. */
function enforceKeyLimit(entries: Record<string, TestDataEntry[]>): Record<string, TestDataEntry[]> {
  const keys = Object.keys(entries);
  if (keys.length <= MAX_KEYS) return entries;

  const sorted = keys
    .map((k) => ({ key: k, lastUsed: entries[k]![0]?.lastUsed ?? 0 }))
    .sort((a, b) => b.lastUsed - a.lastUsed);

  const keep = new Set(sorted.slice(0, MAX_KEYS).map((s) => s.key));
  const next: Record<string, TestDataEntry[]> = {};
  for (const k of keep) {
    next[k] = entries[k]!;
  }
  return next;
}

/** Remove entries older than TTL_MS and drop empty keys. */
function evictStale(entries: Record<string, TestDataEntry[]>): Record<string, TestDataEntry[]> {
  const cutoff = Date.now() - TTL_MS;
  const next: Record<string, TestDataEntry[]> = {};
  for (const [key, list] of Object.entries(entries)) {
    const filtered = list.filter((e) => e.lastUsed >= cutoff);
    if (filtered.length > 0) {
      next[key] = filtered;
    }
  }
  return next;
}

export const useTestDataStore = create<TestDataStore>()(
  persist(
    (set, get) => ({
      version: 1,
      entries: {},

      getPayload: (workflowName, triggerName) => {
        const list = get().entries[makeKey(workflowName, triggerName)];
        return list?.[0];
      },

      setPayload: (workflowName, triggerName, entry) =>
        set((state) => {
          const key = makeKey(workflowName, triggerName);
          const existing = state.entries[key] ?? [];
          // Always stamp lastUsed to prevent stale-on-insert eviction
          const stamped = { ...entry, lastUsed: Date.now() };
          const next = [stamped, ...existing].slice(0, MAX_ENTRIES_PER_KEY);
          const updated = { ...state.entries, [key]: next };
          return { entries: enforceKeyLimit(updated) };
        }),

      clearPayload: (workflowName, triggerName) =>
        set((state) => {
          const key = makeKey(workflowName, triggerName);
          const { [key]: _, ...rest } = state.entries;
          return { entries: rest };
        }),

      cleanup: () =>
        set((state) => ({ entries: enforceKeyLimit(evictStale(state.entries)) })),
    }),
    {
      name: 'otto:test-data',
      partialize: (state) => ({
        version: state.version,
        entries: state.entries,
      }),
      onRehydrateStorage: () => (state) => {
        state?.cleanup();
      },
      storage: {
        getItem: (name) => {
          try {
            const raw = localStorage.getItem(name);
            return raw ? JSON.parse(raw) as { state: TestDataState } : null;
          } catch {
            try { localStorage.removeItem(name); } catch { /* storage unavailable */ }
            return null;
          }
        },
        setItem: (name, value) => {
          try { localStorage.setItem(name, JSON.stringify(value)); } catch { /* quota exceeded or private mode */ }
        },
        removeItem: (name) => {
          try { localStorage.removeItem(name); } catch { /* noop */ }
        },
      },
    }
  )
);
